import React, { useState, createElement, Component } from 'react';
import { Warning } from '@phosphor-icons/react';
import { DownloadIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { EmailVerificationBanner } from '../components/forms/onboarding/EmailVerificationBanner';
import { loadOnboardingDraft } from '../lib/utils';
import { PAGE_ROUTE } from '../lib/routes';
interface DashboardSetariProps {
  navigate: (page: string) => void;
}
function SettingsToggle({
  checked,
  onCheckedChange,
  label,
  description
}: {
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return <div className="flex items-start justify-between gap-4 py-2" data-id="element-3443">
      <div className="min-w-0" data-id="element-3444">
        <p className="text-sm font-medium text-light-1" data-id="element-3445">{label}</p>
        <p className="text-xs text-light-3 mt-0.5" data-id="element-3446">{description}</p>
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} onClick={() => onCheckedChange(!checked)} className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer ${checked ? 'bg-lime-1' : 'bg-dark-4'}`} data-id="element-3447">
        <span className={`inline-block h-4 w-4 rounded-full transition-transform shadow-sm ${checked ? 'translate-x-6 bg-dark-1' : 'translate-x-1 bg-light-1'}`} data-id="element-3448" />
      </button>
    </div>;
}
export function DashboardSetari({
  navigate
}: DashboardSetariProps) {
  const [settings, setSettings] = useState({
    notificationEmail: true,
    notificationPush: false,
    isPublic: true
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const saveSettings = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
    }, 1000);
  };
  const deleteAccount = () => {
    setDeleting(true);
    setTimeout(() => {
      setDeleting(false);
      navigate(PAGE_ROUTE.HOME);
    }, 1500);
  };
  // Mock email verification state from draft
  const draft = loadOnboardingDraft();
  const needsVerification = draft?.account?.method === 'email' && draft?.emailVerified === false;
  return <div className="space-y-8" data-id="element-3449">
      {needsVerification && <div className="-mx-4 sm:-mx-8 -mt-8 mb-8" data-id="element-3450">
          <EmailVerificationBanner email={draft.account.email} data-id="element-3451" />
        </div>}

      <h1 className="text-2xl font-bold text-light-1" data-id="element-3452">Setări</h1>

      {/* Notifications */}
      <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 space-y-5" data-id="element-3453">
        <h2 className="text-lg font-semibold text-light-1" data-id="element-3454">Notificări</h2>

        <SettingsToggle checked={settings.notificationEmail} onCheckedChange={v => setSettings(p => ({
        ...p,
        notificationEmail: v
      }))} label="Notificări email" description="Primești email când un proprietar aplică la profilul tău" data-id="element-3455" />

        <SettingsToggle checked={settings.notificationPush} onCheckedChange={v => setSettings(p => ({
        ...p,
        notificationPush: v
      }))} label="Notificări push" description="Notificări push în browser (în curând)" data-id="element-3456" />
      </div>

      {/* Privacy */}
      <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 space-y-5" data-id="element-3457">
        <h2 className="text-lg font-semibold text-light-1" data-id="element-3458">
          Confidențialitate
        </h2>

        <SettingsToggle checked={settings.isPublic} onCheckedChange={v => setSettings(p => ({
        ...p,
        isPublic: v
      }))} label="Profil public" description="Profilul tău apare în marketplace și e accesibil prin link" data-id="element-3459" />
      </div>

      {/* Save settings CTA */}
      <div data-id="element-3460">
        <Button onClick={saveSettings} disabled={saving} className="bg-lime-1 hover:bg-lime-2 text-dark-1 font-semibold" data-id="element-3461">
          {saving ? <span className="flex items-center gap-2" data-id="element-3462">
              <Spinner size="sm" data-id="element-3463" />
              Se salvează...
            </span> : 'Salvează setările'}
        </Button>
      </div>

      {/* GDPR & Datele mele */}
      <div className="bg-dark-2 border border-dark-4 rounded-2xl p-6 space-y-5" data-id="element-3464">
        <h2 className="text-lg font-semibold text-light-1" data-id="element-3465">Datele mele</h2>
        <p className="text-sm text-light-3 leading-relaxed" data-id="element-3466">
          Conform normelor GDPR, ai dreptul să descarci o copie a tuturor
          datelor tale personale stocate pe platforma Izio.
        </p>
        <Button variant="outline" onClick={() => {
        // Mock download
        const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify({
          user: 'mock_data',
          status: 'active',
          preferences: {}
        }, null, 2));
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute('href', dataStr);
        downloadAnchorNode.setAttribute('download', 'date_izio.json');
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
      }} className="flex items-center gap-2" data-id="element-3467">
          <DownloadIcon className="w-4 h-4" data-id="element-3468" />
          Descarcă datele (JSON)
        </Button>
      </div>

      {/* Danger zone */}
      <div className="bg-dark-2 border border-red-2 rounded-2xl p-6" data-id="element-3469">
        <div className="flex items-start gap-3 mb-4" data-id="element-3470">
          <div className="w-9 h-9 shrink-0 rounded-full bg-red-4 flex items-center justify-center" data-id="element-3471">
            <Warning size={18} weight="fill" className="text-red-1" data-id="element-3472" />
          </div>
          <div className="min-w-0" data-id="element-3473">
            <h2 className="text-base font-semibold text-light-1" data-id="element-3474">
              Ștergere cont
            </h2>
            <p className="text-sm text-light-3 mt-1 leading-relaxed" data-id="element-3475">
              Contul tău va fi dezactivat imediat și șters definitiv după 14
              zile. În această perioadă te poți răzgândi autentificându-te din
              nou. După 14 zile, profilul, datele și toate aplicările tale vor
              fi eliminate ireversibil.
            </p>
          </div>
        </div>

        {!confirmDelete ? <button type="button" onClick={() => setConfirmDelete(true)} className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-red-3 hover:bg-red-2 border border-red-2 text-red-1 transition-colors text-sm font-medium cursor-pointer" data-id="element-3476">
            Șterge contul
          </button> : <div className="rounded-xl border border-red-2 bg-red-4 p-4 space-y-3" data-id="element-3477">
            <p className="text-sm font-medium text-light-1" data-id="element-3478">
              Ești sigur? Această acțiune nu poate fi anulată.
            </p>
            <div className="flex gap-3" data-id="element-3479">
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-dark-3 hover:bg-dark-4 text-light-1 transition-colors text-sm font-medium cursor-pointer disabled:opacity-50" data-id="element-3480">
                Anulează
              </button>
              <button type="button" onClick={deleteAccount} disabled={deleting} className="inline-flex items-center justify-center h-10 px-5 rounded-md bg-red-1 hover:opacity-90 text-light-1 transition-opacity text-sm font-medium cursor-pointer disabled:opacity-50" data-id="element-3481">
                {deleting ? <span className="flex items-center gap-2" data-id="element-3482">
                    <Spinner size="sm" data-id="element-3483" />
                    Se șterge...
                  </span> : 'Da, șterge contul'}
              </button>
            </div>
          </div>}
      </div>
    </div>;
}